import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Eye, User } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function NewsStory() {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchStory();
  }, [slug]);

  const fetchStory = async () => {
    try {
      const response = await fetch(`${API}/news/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setStory(data);
      } else if (response.status === 404) {
        setNotFound(true);
      }
    } catch (error) {
      console.error("Error fetching story:", error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading story...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
          <div className="flex items-center justify-between px-4 py-3 lg:px-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8 sm:h-10" />
              
            </Link>
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-4xl mx-auto text-center py-20">
          <h1 className="font-heading text-4xl font-bold uppercase mb-4">Story Not Found</h1>
          <p className="text-muted-foreground mb-8">The story you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <Link to="/news">
            <Button className="btn-primary">Back to News</Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8 sm:h-10" />
            
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/news">
              <Button variant="ghost" data-testid="news-btn">News</Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" data-testid="login-btn">Login</Button>
            </Link>
            <Link to="/register">
              <Button className="btn-primary" data-testid="register-btn">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-4xl mx-auto">
        {/* Back Link */}
        <Link to="/news" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to News
        </Link>

        <article>
          {/* Featured Image */}
          {story.featured_image && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img 
                src={story.featured_image.startsWith('/api') ? API_BASE + story.featured_image : story.featured_image}
                alt={story.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>
          )}

          {/* Tags */}
          {story.tags && story.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {story.tags.map((tag, idx) => (
                <Badge key={idx} variant="outline" className="text-xs border-primary/50 text-primary">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 className="font-heading text-3xl lg:text-5xl font-bold uppercase mb-4">
            {story.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8 pb-8 border-b border-border">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(story.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {story.author_name}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {story.views || 0} views
            </span>
          </div>

          {/* Content */}
          <div 
            className="prose prose-invert prose-lg max-w-none 
              prose-headings:font-heading prose-headings:uppercase prose-headings:tracking-wide
              prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground
              prose-p:text-muted-foreground prose-p:leading-relaxed
              prose-li:text-muted-foreground
              prose-img:rounded-lg prose-img:my-8
              prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: story.content }}
          />
        </article>

        {/* Share / Back */}
        <div className="mt-12 pt-8 border-t border-border">
          <Link to="/news">
            <Button variant="outline" className="border-primary text-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to News
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-8 lg:px-12 border-t border-border mt-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
            
          </div>
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} TOOLIO. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
